// src/services/authService.js
// All Supabase Auth operations

import { supabase } from '../lib/supabase'

/**
 * Sign in with email + password
 * Returns { user, profile, error }
 */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) return { user: null, profile: null, error: error.message }

  const profile = await fetchProfile(data.user.id)
  return { user: data.user, profile, error: null }
}

/**
 * Sign up a new user (admin only creates users in Phase 2)
 * Returns { user, error }
 */
export async function signUp(email, password, metadata = {}) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: metadata },
  })
  if (error) return { user: null, error: error.message }
  return { user: data.user, error: null }
}

/**
 * Sign out current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  return { error: error?.message || null }
}

/**
 * Get current session (used on app load)
 * Returns { user, profile } or { user: null, profile: null }
 */
export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return { user: null, profile: null }

  const profile = await fetchProfile(session.user.id)
  return { user: session.user, profile }
}

/**
 * Fetch profile row for a user id
 */
export async function fetchProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) { console.error('Profile fetch error:', error); return null }
  return data
}

/**
 * Update profile fields
 */
export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()

  if (error) return { data: null, error: error.message }
  return { data, error: null }
}

/**
 * Change password for current user
 */
export async function changePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  return { error: error?.message || null }
}

/**
 * List all users (admin only — requires service role in production)
 * In Phase 2 we use profiles table which is accessible via RLS
 */
export async function listProfiles() {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: true })

  if (error) return { data: [], error: error.message }
  return { data, error: null }
}
